import { type AxiosResponse } from 'axios'

import ApiService, { type RequestOptions } from '@/services/api.service'

import { TagType } from '@/constants/tags'
import {
  type Tag,
  type TagListResponse,
  type TagUpdate,
  type AssignTagsResponse,
} from '@/types/services/tags'
import type { TagModelValue } from '@/types/components/tags'

class AccountTag {
  /**
   * Create Tag
   * @param entityId ID of entity.
   * @param tag Tag to create.
   **/
  static async createTag(
    entityId: string,
    tag: Omit<Tag, 'entity_id'>
  ): Promise<AxiosResponse<Tag>> {
    return ApiService.post('/api/v3/funds-core/tags', {
      ...tag,
      entity_id: entityId,
    })
  }

  /**
   * Update Tag
   * @param tag Tag to update
   **/
  static async updateTag(tag: TagUpdate): Promise<AxiosResponse<Tag>> {
    const { id, ...data } = tag
    return ApiService.patch(`/api/v3/funds-core/tags/${id}`, data)
  }

  /**
   * Delete Tag
   * @param tagId ID of tag.
   **/
  static async deleteTag(tagId: string): Promise<AxiosResponse<void>> {
    return ApiService.delete(`/api/v3/funds-core/tags/${tagId}`)
  }

  /**
   * Assign account target tag to accounts with source tag and then remove source tag.
   * @param sourceTagId ID of source tag
   * @param targetTagId ID of target tag
   */
  static async mergeTags(sourceTagId: string, targetTagId: string) {
    return ApiService.post(`/api/v1/entities/merge_tags`, {
      source_tag_id: sourceTagId,
      target_tag_id: targetTagId,
    })
  }

  /**
   * Assign Account Tags to Account (in batch)
   * @param accountId ID of account.
   * @param tags Array of tags that assign to the account.
   **/
  static async assignAccountTags(
    accountId: string,
    tags: TagModelValue[]
  ): Promise<AxiosResponse<AssignTagsResponse>> {
    const validTags = tags.map((tag) => {
      const _tag = {
        tag_id: tag.id, // empty for new tags
        tag_name: tag.name,
        type: TagType.TAG,
      }
      return _tag
    })

    return ApiService.post(`/api/v1/accounts/${accountId}/tags`, {
      tags: validTags,
    })
  }

  /**
   * Remove Account Tag from Account
   * @param accountId ID of account.
   * @param tagId ID of tag.
   **/
  static async removeAccountTag(
    accountId: string,
    tagId: string
  ): Promise<AxiosResponse<any>> {
    return ApiService.delete(`/api/v1/accounts/${accountId}/tags/${tagId}`)
  }

  /**
   * Get Tags with their usage counts
   * @param entityId ID of entity.
   **/
  static async getTags(entityId: string): Promise<AxiosResponse<Tag[]>> {
    return ApiService.get(`/api/v3/funds-core/tags?entity_id=${entityId}`)
  }

  /**
   * Get Tags of an entity for the One Pager
   * @param entityId ID of entity.
   * @param options Request options (headers, etc.)
   **/
  static async getTagsForEntity(
    entityId: string,
    options?: RequestOptions
  ): Promise<AxiosResponse<TagListResponse>> {
    return ApiService.post(
      `/api/v1/tag/search/get_tag_list`,
      { aggr: { params: { entity_id: entityId } } },
      options
    )
  }
}

export default AccountTag
